// ChannelHealthWidget — Polish #20.
//
// Two-in-one component:
//   1. A red banner that renders ONLY when overall is 'error'. Positioned
//      at the very top of Standup so a broken poller can't hide. Kimi is
//      solo, so a silent Gmail outage would cost her customers.
//   2. A small dashboard widget showing a pill per channel (gmail, etsy md,
//      etsy hk, shipstation) with its health color + one-line message.
//      Renders inline under the Standup hero so it's always visible but
//      doesn't compete with the bucket headlines.

import { useChannelHealth } from '@/lib/hooks/useChannelHealth';
import type { ChannelHealthItem } from '@/lib/hooks/useChannelHealth';

function statusDotClass(status: ChannelHealthItem['status']): string {
  switch (status) {
    case 'healthy':
      return 'ch-health-dot ch-health-dot--ok';
    case 'warning':
      return 'ch-health-dot ch-health-dot--warn';
    case 'error':
      return 'ch-health-dot ch-health-dot--err';
  }
}

/** How long ago an ISO string was, in human-friendly short form. */
function ago(iso: string | null): string {
  if (!iso) return 'never';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 0) return 'just now';
  if (diff < 60 * 1000) return 'just now';
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function ChannelHealthBanner() {
  const { data } = useChannelHealth();
  if (!data || data.overall !== 'error') return null;

  const broken = data.items.filter((i) => i.status === 'error');
  const names = broken.map((i) => i.label).join(', ');
  return (
    <div className="ch-health-banner" role="alert">
      <strong>Channel outage:</strong> {names}. Ingestion may be stalled until this is fixed.
    </div>
  );
}

export function ChannelHealthWidget() {
  const { data, isPending } = useChannelHealth();

  if (isPending) {
    return (
      <div className="ch-health-widget ch-health-widget--pending" aria-busy>
        <span>Loading channel health...</span>
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className={`ch-health-widget ch-health-widget--${data.overall}`}>
      <div className="ch-health-widget-head">
        <span className="ch-health-widget-label">Channel health</span>
        <span className="ch-health-widget-ts">as of {ago(data.generatedAt)}</span>
      </div>
      <ul className="ch-health-list" role="list">
        {data.items.map((item) => (
          <li
            key={`${item.channel}-${item.label}`}
            className={`ch-health-row ch-health-row--${item.status}`}
            title={item.message}
          >
            <span className={statusDotClass(item.status)} aria-hidden />
            <span className="ch-health-name">{item.label}</span>
            <span className="ch-health-sub">
              {item.connected
                ? item.lastSuccessAt
                  ? `last ok ${ago(item.lastSuccessAt)}`
                  : 'never synced'
                : 'not connected'}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
